import { z } from 'zod'

// ─── Shared ───────────────────────────────────────────────────────────────────

import { HazardCatalog, HazardProjection } from '@app/domains/hazards/types/hazard.types'
import { HazardLayerMetadata } from '@app/types'

export type IconRef = string
export type TilesetType = 'vector' | 'line' | 'point' | 'icon' | string
export type MetricType = 'count' | 'area' | 'length' | string
export type FilterableInformationType = 'text' | 'numeric' | string

// ─── Elements ─────────────────────────────────────────────────────────────────

export type ElementTileset = {
  id: string
  icon: IconRef | null
  type: TilesetType
  style: string | null
  sourceLayer: string
  has_extrusion: boolean
}

export type FilterableInformationField = {
  key: string
  type: FilterableInformationType
  label: string
  lookup?: boolean
}

export type ElementItem = {
  icon: IconRef
  type: string
  display_name: string
  legend: string
  unit: string
  metric_type: MetricType
  source: string
  provider: string
  date_added: string
  date_updated: string
  helpFileName: string | null
  tilesets: ElementTileset[]
  filterable_information: FilterableInformationField[] | null
  [k: string]: unknown
}

export type ElementGroup = {
  icon: IconRef
  groupName: string
  assets: ElementItem[]
}

export type ElementCategory = {
  icon: IconRef
  title: string
  groups: ElementGroup[]
}

export type ElementCatalog = Record<string, ElementCategory>

// ─── Contextuals ──────────────────────────────────────────────────────────────

export type ContextualTilesetType = 'vector' | 'point' | 'line' | string

export type ContextualTileset = {
  id: string
  icon: IconRef | null
  type: ContextualTilesetType
  sourceLayer: string
}

export type ContextualLayer = {
  id: string
  icon: IconRef
  legend: string
  tilesets: ContextualTileset[]
  display_name: string
}

export type ContextualGroup = {
  icon: IconRef
  groupName: string
  layers: ContextualLayer[]
}

export type ContextualCategory = {
  icon: IconRef
  title: string
  groups: ContextualGroup[]
}

export type ContextualCatalog = Record<string, ContextualCategory>

// ─── Catalog ──────────────────────────────────────────────────────────────────

export interface Layers {
  elements: ElementCatalog
  hazards: {
    hazardDetails: HazardCatalog
    projections: HazardProjection[]
  }
  contextuals: ContextualCatalog
  layerMetadata: HazardLayerMetadata[]
}
// ─── Layer detail ─────────────────────────────────────────────────────────────

export interface LayerSection {
  title: string
  description: string
}

export interface LayerMetaDetail {
  label: string
  value: string
}

export interface LayerImage {
  url?: string
  title?: string
  description?: string
}

export interface LayerAccordion {
  title?: string
  content?: string
}

export interface LayerSource {
  title?: string
  url?: string
}

export interface LayerDetail {
  title: string
  icon: string
  startsOpen: boolean
  images: LayerImage[]
  sections: LayerSection[]
  accordions: LayerAccordion[]
  details: LayerMetaDetail[]
  sources: LayerSource[]
  layer_id: string
}

export const layerMetadataSchema = z.object({
  title: z.string().optional(),
  description_md: z.string().optional(),
  provider: z.string().optional(),
  spatial_extent: z.string().optional(),
  created_at: z.string().optional(),
  owner_name: z.string().optional(),
  owner_organization: z.string().optional(),
  owner_email: z.string().email('Must be a valid email').optional().or(z.literal('')),
  confidence_rating: z.enum(['A', 'B', 'C', 'D']).optional().or(z.literal('')),
  limitations_caveats_md: z.string().optional(),
  source_links: z
    .array(
      z.object({
        title: z.string().min(1, 'Title is required'),
        url: z.string().url('Must be a valid URL'),
      }),
    )
    .optional(),
  license: z
    .object({
      title: z.string().optional(),
      url: z.string().url('Must be a valid URL').optional().or(z.literal('')),
    })
    .optional(),
  additional_notes_md: z.string().optional(),
})

export type LayerMetadataFormValues = z.infer<typeof layerMetadataSchema>
